import { IbanElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Form, Formik } from 'formik';
import styled, { useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Label } from '@hivebrite/design-system/Label';

import { STRIPE_PAYMENT_TYPES } from 'hb-react/shared/components/Payment/constants';
import { usePaymentContext } from 'hb-react/shared/components/Payment/context/PaymentContext';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import MandateText from '../MandateText';

const IBAN_STYLE = (theme) => ({
  base: {
    color: theme.input.color,
    iconColor: theme.input.iconColor,
    fontSize: '16px',
    '::placeholder': {
      color: theme.input.placeholder,
    },
    ':-webkit-autofill': {
      color: '#32325d',
    },
  },
  invalid: {
    color: theme.input.error.color,
    iconColor: theme.input.error.color,
    ':-webkit-autofill': {
      color: '#fa755a',
    },
  },
});

const IBANForm = () => {
  const {
    paymentAccountConfig,
    paymentEndpoint,
    paymentData,
    billingData,
    amountFormatted,
    isLoading,
    setLoading,
    onSuccess,
  } = usePaymentContext();

  const { external_account_id: stripeAccount, type: gateway } =
    paymentAccountConfig;

  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();

  const handleError = (error) => {
    displayNotif(
      'error',
      s__('StripeSepaDebit|An error occurred processing the payment'),
    );
    setLoading(false);
    errorReporting.captureException(error);
  };

  const initializePayment = (setupIntent) => ({
    ...paymentData,
    billing: {
      transaction_token: setupIntent.id,
      gateway,
      payment_method: STRIPE_PAYMENT_TYPES.SEPA_DEBIT,
      async_payment: true,
    },
  });

  const executePayment = (setupIntent) =>
    axios
      .post(paymentEndpoint, initializePayment(setupIntent))
      .then(({ data }) => {
        if (onSuccess) {
          onSuccess(data);
        }
      });

  const confirmSepaDebitSetup = (data) => {
    const iban = elements.getElement(IbanElement);

    stripe
      .confirmSepaDebitSetup(data.client_secret, {
        payment_method: {
          sepa_debit: iban,
          billing_details: billingData,
        },
      })
      .then(({ setupIntent, error }) => {
        if (error) {
          handleError(
            new Error('Error confirming sepa debit setup intent', error),
          );
        } else {
          executePayment(setupIntent);
        }
      });
  };

  const createSepaDebitSetupIntent = () => {
    axios
      .post(Routes.stripe_setup_intent_path(), {
        external_account_id: stripeAccount,
        payment_method_types: ['sepa_debit'],
      })
      .then(({ data }) => {
        confirmSepaDebitSetup(data);
      })
      .catch((error) => {
        handleError(new Error('Error creating setup intent', error));
      });
  };

  const handleSubmit = () => {
    setLoading(true);

    createSepaDebitSetupIntent();
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      validateOnMount
      initialValues={{ iban: '' }}
      validate={({ iban }) => {
        if (!iban.complete) {
          return {
            error: 'iban',
          };
        }
      }}
    >
      {({ isValid, setFieldValue }) => (
        <Form>
          <Wrapper data-testid="stripe-sepa-debit-element">
            <Box display="block" data-testid="stripe-iban">
              <Label htmlFor="iban-bank-element" required>
                {s__('StripeIdeal|IBAN')}
              </Label>
              <IbanElement
                id="iban-bank-element"
                options={{
                  supportedCountries: ['SEPA'],
                  style: IBAN_STYLE(theme),
                }}
                onChange={(element) => setFieldValue('iban', element)}
              />
            </Box>
            <MandateText
              merchantDisplayName={paymentAccountConfig.merchant_display_name}
            />
            <Box
              justifyContent="space-between"
              alignContent="center"
              width="100%"
              mt={3}
            >
              <Box ml="auto" pl={3}>
                <Button
                  dataTestId="stripe-sepa-debit-form-submit"
                  type="submit"
                  isDisabled={!isValid}
                  isLoading={isLoading}
                >
                  {__('Pay %{amountFormatted}').i__({
                    amountFormatted,
                  })}
                </Button>
              </Box>
            </Box>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

export default IBANForm;

const Wrapper = styled(Box)`
  flex-direction: column;
  row-gap: 12px;
`;
